import { takeWhile } from 'rxjs'

import Base from '../paperTool/PaperTool'
import { DRAWING_TYPES } from '../../../../../../shared/constants/drawable-types'
import { Cursors, IMUPState, PaperToolConfig, VIEW_MODE } from '../../../../../types'

/**
 * Border.tool.ts
 * This tool is used to draw materials around the border of other materials.
 */
export class Border extends Base {
    static NAME = 'BORDER'
    static CURSOR = Cursors.CELL
    static ANIMATION_MAX_OFFSET = 0.99
    static ANIMATION_INCREMENT = 0.01
    static UNION_OVERLAP_PX = 1
    static UNION_EXPAND_PX = 6

    private selectedItemDictionary: Record<number, paper.Path | paper.CompoundPath> = {}
    private temporaryPathPolygons: (paper.Path | paper.CompoundPath)[] = []
    private temporaryLinePaths: paper.Path[] = []
    private lineColor: paper.Color = new this.paper.Color('red')
    private secondaryLineColor: paper.Color = new this.paper.Color('green')
    private tertiaryLineColor: paper.Color = new this.paper.Color('blue')

    constructor(config: PaperToolConfig) {
        super(config)
        this.name = Border.NAME
        this.cursor = Border.CURSOR

        this.mediator.get$().pipe(takeWhile((state: IMUPState) => state.common.activeMode === VIEW_MODE.Markup2D))
    }

    onMouseDown = (event: paper.ToolEvent): void => {
        if (this.isPanningClick(event)) {
            return
        }

        const hit = this.getPaperScope().project.hitTest(event.point)

        // Filter out highlights, regions, and other items that shouldn't be selectable by the perimeter tool
        if (hit && hit.item && this.shouldIncludeItem(hit)) {
            if (this.selectedItemDictionary[hit.item.id]) {
                delete this.selectedItemDictionary[hit.item.id]
            } else {
                this.selectedItemDictionary[hit.item.id] = hit.item as paper.Path | paper.CompoundPath
            }
            this.drawBorders()
        }
    }

    /**
     * On mouse drag if it is right click
     * or middle click then pan
     * @param event the paper mouse event
     */
    onMouseDrag = (event: paper.ToolEvent): void => {
        this.toolPanning(event)
    }

    private finalizeSelection = (): void => {
        this.setState('2D', { drawablesToCreate: this.temporaryLinePaths.map((path) => path.id) })
    }

    onActivate = (): void => {
        this.selectedItemDictionary = {}
        this.temporaryPathPolygons = []
        this.temporaryLinePaths.forEach((path) => path.remove())
        this.temporaryLinePaths = []
    }

    cancel = (): void => {
        this.selectedItemDictionary = {}
        this.temporaryPathPolygons = []
        this.temporaryLinePaths.forEach((path) => path.remove())
        this.temporaryLinePaths = []
    }

    /**
     * Check if an item is a drawable that should be selectable by the border tool.
     * Instead of using a blacklist approach, this checks for the presence of the isEnabledForBorderTool flag.
     * @param hit HitResult from paper.js hitTest
     * @returns boolean - true if the item is a drawable enabled for the border tool, false otherwise
     */
    private shouldIncludeItem = (hit: paper.HitResult): boolean => {
        return hit.item.data.isEnabledForBorderTool === true
    }

    /**
     * Expand each path slightly before uniting so that touching shapes
     * (zero-area intersection, e.g. two floor systems sharing a wall) produce
     * a single merged outline rather than a CompoundPath with separate children
     * @param group
     */
    private uniteGroup = (group: (paper.Path | paper.CompoundPath)[]): paper.Path | paper.CompoundPath => {
        if (group.length === 1) return group[0]

        const expandedPaths = group.map((path) => {
            const clone = path.clone({ insert: false }) as paper.Path | paper.CompoundPath
            const { width, height, center } = clone.bounds

            clone.scale((width + Border.UNION_EXPAND_PX) / width, (height + Border.UNION_EXPAND_PX) / height, center)

            return clone
        })

        return expandedPaths.slice(1).reduce((mergedPath, nextPath) => {
            const united = mergedPath.unite(nextPath, { insert: false }) as paper.Path | paper.CompoundPath

            mergedPath.remove()
            nextPath.remove()

            return united
        }, expandedPaths[0] as paper.Path | paper.CompoundPath)
    }

    private drawBorders = (): void => {
        if (this.temporaryLinePaths.length) {
            this.temporaryLinePaths.forEach((path) => path.remove())
            this.temporaryLinePaths = []
        }

        if (this.temporaryPathPolygons.length) {
            this.temporaryPathPolygons = []
        }

        const selectedPaths = Object.values(this.selectedItemDictionary)

        if (selectedPaths.length === 0) {
            return
        }

        const groupedPaths = this.groupAllIntersectingPaths(selectedPaths)

        groupedPaths.forEach((group) => {
            this.temporaryPathPolygons.push(this.uniteGroup(group))
        })

        this.temporaryPathPolygons.forEach((shape) => {
            const contours = this.extractAllSegmentsFromPath(shape)

            contours.forEach((contourSegments) => {
                // Drop segments whose distance to the next vertex is below the threshold —
                // these are expansion artifacts introduced by uniteGroup's slight scale-up
                const segmentsWithoutExpansionArtifacts = contourSegments.filter((seg, i) => {
                    const nextSegment = contourSegments[i + 1] ?? contourSegments[0]

                    return seg.point.getDistance(nextSegment.point) >= Border.UNION_EXPAND_PX
                })

                this.temporaryLinePaths.push(...this.convertPathSegmentsToPaths(segmentsWithoutExpansionArtifacts))
            })
        })

        this.paper.project.activeLayer.addChildren(this.temporaryLinePaths)

        this.finalizeSelection()
    }

    /**
     * Extracts segments from either a simple Path or CompoundPath.
     * For CompoundPath (materials with cutouts), it gets segments from ALL children (main shape + cutouts).
     * For simple Path, it returns the path's segments directly.
     * @param shape The path or compound path to extract segments from
     * @returns Array of segment arrays representing all borders to draw around (main shape and cutouts)
     */
    private extractAllSegmentsFromPath = (shape: paper.Path | paper.CompoundPath): paper.Segment[][] => {
        // Check if this is a CompoundPath (materials with cutouts)
        if (shape.children && shape.children.length > 0) {
            // For CompoundPath, get segments from ALL children (main shape + all cutouts)
            return shape.children
                .map((child) => (child as paper.Path).segments)
                .filter((segments) => segments && segments.length > 0)
        }

        // For simple Path, return segments as a single array within an array (if it has segments)
        const segments = (shape as paper.Path).segments

        return segments && segments.length > 0 ? [segments] : []
    }

    private convertPathSegmentsToPaths = (segments: paper.Segment[]): paper.Path[] => {
        return segments.map((segment, index) => {
            const nextSegment = segments[index + 1] || segments[0]
            const gradientColor: paper.Color = new this.paper.Color({
                gradient: {
                    stops: [
                        [this.lineColor, 0],
                        [this.secondaryLineColor, 0.33],
                        [this.tertiaryLineColor, 0.66],
                    ],
                    radial: false,
                },
                origin: segment.point,
                destination: nextSegment.point,
            })
            const path = new this.paper.Path.Line({
                from: segment.point,
                to: nextSegment.point,
                insert: false,
                strokeColor: gradientColor,
                strokeWidth: this.strokeWidth,
                data: {
                    shapeType: DRAWING_TYPES.SECTION,
                },
                onFrame: () => {
                    // To animate the temporary paths so it is clear they are being created
                    if (!path.strokeColor) return
                    path.strokeColor.gradient.stops[0].offset =
                        path.strokeColor.gradient.stops[0].offset < Border.ANIMATION_MAX_OFFSET
                            ? path.strokeColor.gradient.stops[0].offset + Border.ANIMATION_INCREMENT
                            : 0
                    path.strokeColor.gradient.stops[1].offset =
                        path.strokeColor.gradient.stops[1].offset < Border.ANIMATION_MAX_OFFSET
                            ? path.strokeColor.gradient.stops[1].offset + Border.ANIMATION_INCREMENT
                            : 0
                    path.strokeColor.gradient.stops[2].offset =
                        path.strokeColor.gradient.stops[2].offset < Border.ANIMATION_MAX_OFFSET
                            ? path.strokeColor.gradient.stops[2].offset + Border.ANIMATION_INCREMENT
                            : 0
                },
            })

            path.data.parentSystem = this.identifyParentSystem(path)

            return path
        })
    }

    /**
     * Returns true if two paths overlap (intersect) or are touching/adjacent.
     * paper.js getIntersections only detects crossing paths, not shared/coincident edges between
     * adjacent shapes (e.g. two floor/roof systems sharing a wall). The bounding-box gap check
     * catches those cases: shapes whose bounding boxes are within `UNION_OVERLAP_PX` of each other
     * in both axes are considered connected.
     */
    private pathsAreConnected = (
        path1: paper.Path | paper.CompoundPath,
        path2: paper.Path | paper.CompoundPath,
        tolerance = Border.UNION_OVERLAP_PX
    ): boolean => {
        if (path1.getIntersections(path2).length > 0) return true

        const b1 = path1.bounds
        const b2 = path2.bounds
        const dx = Math.max(b1.left - b2.right, b2.left - b1.right, 0)
        const dy = Math.max(b1.top - b2.bottom, b2.top - b1.bottom, 0)

        return dx <= tolerance && dy <= tolerance
    }

    /**
     * Groups paths that intersect with each other into arrays of paths.
     * This is useful for creating a single drawable from multiple intersecting paths.
     * @param paths The array of paper.Path or paper.CompoundPath objects to group
     * @returns An array of arrays, where each inner array contains paths that intersect with each other
     */
    private groupAllIntersectingPaths = (
        selectedPaths: (paper.Path | paper.CompoundPath)[]
    ): (paper.Path | paper.CompoundPath)[][] => {
        const processedPathIds = new Set<number>()
        const intersectionGroups: (paper.Path | paper.CompoundPath)[][] = []

        for (const startingPath of selectedPaths) {
            if (processedPathIds.has(startingPath.id)) continue

            const connectedPaths: (paper.Path | paper.CompoundPath)[] = []
            const pathsToCheck: (paper.Path | paper.CompoundPath)[] = [startingPath]

            while (pathsToCheck.length > 0) {
                const currentPath = pathsToCheck.shift()

                if (!currentPath) continue

                if (processedPathIds.has(currentPath.id)) continue
                processedPathIds.add(currentPath.id)
                connectedPaths.push(currentPath)

                for (const candidatePath of selectedPaths) {
                    if (
                        currentPath.id !== candidatePath.id &&
                        !processedPathIds.has(candidatePath.id) &&
                        this.pathsAreConnected(currentPath, candidatePath) &&
                        !pathsToCheck.some((p) => p.id === candidatePath.id)
                    ) {
                        pathsToCheck.push(candidatePath)
                    }
                }
            }

            if (connectedPaths.length > 0) {
                intersectionGroups.push(connectedPaths)
            }
        }

        return intersectionGroups
    }

    private identifyParentSystem = (child: paper.Path): paper.Path | null => {
        const intersections = Object.values(this.selectedItemDictionary).map((path) => path.getIntersections(child))
        const mostIntersected = intersections.reduce(
            (acc, current) => (current.length > acc.length ? current : acc),
            []
        )

        return mostIntersected.length > 0 ? mostIntersected[0].path : null
    }
}
